/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import styles from "./Modal.module.scss";

import useOutsideClick from "../../hooks/useOutsideClick";
import Portal from "../Portal";
import ModalHeader from "./ModalHeader";
import ModalContent from "./ModalContent/ModalContent";
import ModalActions from "./ModalActions/ModalActions";
import { hideModal, showModal } from "./utils";

export const Modal = ({
  id,
  title,
  children,
  shouldCloseOnOverlayClick,
  shouldCloseOnEsc,
  hasCloseButton,
  className,
  onClose,
  ...rest
}) => {
  const [isShown, setIsShown] = useState(false);
  const [modal, setModal] = useState();
  const [modalCover, setModalCover] = useState();

  useEffect(() => {
    function handleShow(e) {
      const { id: dispatchedId } = e.detail;
      if (id === dispatchedId) {
        setIsShown(true);
      }
    }

    function handleHide(e) {
      const { id: dispatchedId } = e.detail;
      if (id === dispatchedId) {
        setIsShown(false);
        onClose(dispatchedId);
      }
    }

    document.addEventListener("showModal", handleShow);
    document.addEventListener("hideModal", handleHide);

    return () => {
      document.removeEventListener("showModal", handleShow);
      document.removeEventListener("hideModal", handleHide);
    };
  }, [id, onClose]);

  useEffect(() => {
    if (modalCover) {
      modalCover.focus();
    }
  }, [modalCover]);

  const closeModal = useCallback(() => {
    hideModal(id);
  }, [id]);

  const handleKeyDown = useCallback(
    event => {
      if (!shouldCloseOnEsc) {
        return;
      }
      if (event.keyCode === 27) {
        closeModal();
      }
    },
    [shouldCloseOnEsc, closeModal]
  );

  useOutsideClick([modal], () => {
    if (!shouldCloseOnOverlayClick) {
      return;
    }

    closeModal();
  });

  if (!isShown) return null;

  return (
    <Portal>
      <div
        role="dialog"
        tabIndex="-1"
        aria-modal="true"
        className={styles.modalCover}
        onKeyDown={handleKeyDown}
        ref={setModalCover}
      >
        <div className={cx(styles.modalArea, className)} ref={setModal} {...rest}>
          {title && (
            <ModalHeader closeModal={closeModal} hasCloseButton={hasCloseButton}>
              {title}
            </ModalHeader>
          )}
          {children}
        </div>
      </div>
    </Portal>
  );
};

Modal.hide = hideModal;
Modal.show = showModal;
Modal.Actions = ModalActions;
Modal.Content = ModalContent;

Modal.defaultProps = {
  title: undefined,
  children: undefined,
  className: undefined,
  shouldCloseOnOverlayClick: true,
  shouldCloseOnEsc: false,
  hasCloseButton: true,
  onClose: () => {}
};

Modal.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  shouldCloseOnOverlayClick: PropTypes.bool,
  shouldCloseOnEsc: PropTypes.bool,
  hasCloseButton: PropTypes.bool,
  onClose: PropTypes.func
};

export default Modal;
